<template>
  <div class="pa-4">
    <div>
      <v-skeleton-loader v-if="loading" class="mx-auto" type="list-item-three-line"></v-skeleton-loader>
      <div v-if="!loading && !syllabusInstances" class="text-h6 text-center">
        {{ $t("message.no_data") }}
      </div>
      <div v-else-if="!loading">
        <v-expansion-panels v-model="syllabusInstancesPanel" flat class="bjjlink-card" v-if="syllabusInstances"
          v-allowed="'manage_syllabus_instances'">
          <v-expansion-panel :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'"
            v-for="(i, index) in syllabusInstances" :key="'instance_' + index + '_' + i._id">
            <v-expansion-panel-header @click="getSyllabusInstance(i)">
              {{ i.syllabusTemplate.name }}
              <span v-if="i.currentLevel" class="supporting-text" style="margin-left: 10px">{{
                $t("message.current_level") }}: {{ i.syllabusTemplate.useBelts ? $t("message." + i.currentLevel) :
                  i.currentLevel }}</span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div>
                <div v-if="isSmallerDisplay()">
                  <v-btn outlined block class="mt-1" elevation="0" small color="primary"
                    @click="completeInstanceDialog = true">
                    {{ $t("message.activate") }}
                  </v-btn>
                </div>
                <v-card-actions v-else>
                  <v-spacer></v-spacer>
                  <v-btn outlined elevation="0" small color="primary" @click="completeInstanceDialog = true">
                    {{ $t("message.activate") }}
                  </v-btn>
                </v-card-actions>
                <v-card-text>
                  <v-list v-if="selectedInstance && selectedInstance.data && selectedInstance.data.length > 0">
                    <template v-for="(d, index) in selectedInstance.data">
                      <v-subheader :key="'syllabus_instance_header_' + index + '_' + d._id">
                        <v-progress-linear v-if="selectedInstance.syllabusTemplate.useBelts"
                          :color="getBeltColorForProgress(d.level)" height="20" :value="d.overallPercent" striped
                          :style="(d.level && d.level.indexOf('white') >= 0 ? 'border: 1px solid gray;' : '') + (selectedInstance.currentLevel == d.level ? '' : 'opacity: 0.5;')">
                          <template v-slot:default="{ value }">
                            <strong>{{ Math.ceil(value) }}%</strong>
                          </template>
                        </v-progress-linear>
                        <v-progress-linear v-else
                          :color="selectedInstance.colors && selectedInstance.colors[d.level] ? selectedInstance.colors[d.level] : getColor('light')"
                          height="20" :value="d.overallPercent" striped
                          :style="(selectedInstance.colors && selectedInstance.colors[d.level] ? 'color:white;' : '') + (selectedInstance.currentLevel == d.level ? '' : 'opacity: 0.5;')">
                          <template v-slot:default="{ value }">
                            <strong> {{ d.level }}: {{ Math.ceil(value) }}%</strong>
                          </template>
                        </v-progress-linear>
                      </v-subheader>
                      <v-list-item :key="'syllabus_instance_header_time_' + index + '_' + d._id" v-if="d.createdOn">
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ $t('message.started_on') }}: {{ $d((new Date(d.createdOn)), "month_day_year",
                              $i18n.locale) }}
                          </v-list-item-title>
                          <v-list-item-subtitle>
                            {{ $t('message.days_ago', [daysBetween(new Date(), new Date(d.createdOn))]) }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item style="border-bottom:1px solid #D3D3D3" @click="doNothing()"
                        v-for="(rule, ruleIndex) in d.instanceRules"
                        :key="'syllabus_instance_data_rule_' + ruleIndex + '_' + d._id" class="text-left">
                        <v-list-item-content>
                          <span v-if="rule.ruleType == 'checkins'">
                            <LocationMarkerIcon color="gray" size="20"></LocationMarkerIcon>
                            {{ $t('message.regular_checkins') }}
                          </span>
                          <span v-else-if="rule.ruleType == 'checkins_contains'">
                            <LocationMarkerIcon color="gray" size="20"></LocationMarkerIcon>{{ rule.ruleId }}
                          </span>
                          <span v-else-if="rule.ruleType == 'checkins_matches'">
                            <LocationMarkerIcon color="gray" size="20"></LocationMarkerIcon>{{ rule.ruleId }}
                          </span>
                          <span v-else-if="rule.ruleType == 'number'">
                            <ChevronDoubleUpIcon color="grey" size="20"></ChevronDoubleUpIcon>{{ rule.ruleId }}
                          </span>
                          <span v-else-if="rule.ruleType == 'boolean'">
                            <CheckCircleIcon color="gray" size="20"></CheckCircleIcon>{{ rule.ruleId }}
                          </span>
                          <span v-else-if="rule.ruleType == 'days'">
                            <v-icon>mdi-calendar-clock</v-icon>{{ rule.ruleId }}
                          </span>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-text-field outlined v-if="rule.ruleId == 'checkins'" :disabled="true"
                            v-model="rule.numberValue" :label="'Total: ' + rule.numberNeeded"></v-text-field>
                          <v-text-field outlined dense v-else-if="rule.numberValue != null" :disabled="true"
                            v-model="rule.numberValue" :label="'Total: ' + rule.numberNeeded">
                          </v-text-field>
                          <v-switch v-else :disabled="true" v-model="rule.booleanValue"></v-switch>
                        </v-list-item-action>
                      </v-list-item>
                    </template>
                  </v-list>
                  <div v-else class="supporting-text">
                    {{ $t("message.no_current_level") }}
                  </div>
                </v-card-text>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </div>
    <v-dialog v-model="completeInstanceDialog" max-width="90%">
      <v-card class="text-left" elevation="0" outlined v-if="selectedInstance">
        <v-card-title>
          {{ $t("message.please_confirm") }}
        </v-card-title>
        <v-card-actions>
          <v-btn outlined small elevation="0" color="primary" @click="activateInstance()">{{
            $t("message.activate")
            }}</v-btn>
          <v-btn small elevation="0" @click="completeInstanceDialog = false">{{
            $t("message.cancel")
            }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Api from "@/services/Api";

import {
  LocationMarkerIcon,
  CheckCircleIcon,
  ChevronDoubleUpIcon,
} from '@vue-hero-icons/outline'

export default {
  components: {
    LocationMarkerIcon,
    CheckCircleIcon,
    ChevronDoubleUpIcon,
  },
  data() {
    return {
      memberId: this.$route.params.memberid,
      loading: true,
      selectedInstance: null,
      syllabusInstances: null,
      syllabusInstancesPanel: null,
      completeInstanceDialog: null,
    };
  },
  created() {
    this.getSyllabusInstances();
  },
  methods: {
    activateInstance() {
      Api()
        .patch(
          "syllabus/academy/" + this.$store.state.managedAcademy + "/instances/" +
          this.selectedInstance._id + "/complete"
        )
        .then((response) => {
          const data = response.data;
          if (data) {
            this.showAlert("success", this.$t("message.success"));
            //head back to member
            this.getSyllabusInstances();
            this.completeInstanceDialog = false;
          }
        })
        .catch(() => {
          this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    getSyllabusTemplate(instance) {
      Api()
        .get("syllabus/academy/" + this.$store.state.managedAcademy + "/templates/" + instance.syllabusTemplate._id)
        .then(response => {
          if (response) {
            this.selectedTemplate = response.data.template;
            if (this.selectedTemplate && this.selectedTemplate.levels) {
              let levelsPicker = [];
              let position = !instance.currentLevel ? 0 : null;
              for (const l of this.selectedTemplate.levels) {
                if (position >= 0 && l.level != instance.currentLevel) {
                  levelsPicker.push({ text: l.level, value: l.level });
                }
                if (instance.currentLevel && instance.currentLevel == l.level)
                  position = l.position;
              }
              this.selectedTemplate.levelsPicker = levelsPicker;
            }
          }
        })
        .catch(e => {
          if (e)
            this.showAlert("error", "Oops!", this.$t('message.error_generic'));
        });
    },
    getSyllabusInstance(instance) {
      this.selectedInstance = instance;
      this.canLevelUp = false;
      Api()
        .get("syllabus/academy/" + this.$store.state.managedAcademy + "/instances/" + instance._id)
        .then(response => {
          if (response.data) {
            if (response.data.instance.data)
              this.selectedInstance.data = response.data.instance.data.reverse();
            this.selectedInstance.colors = response.data.colors;
            const templateRulesDefinitions = response.data.templateLevelRules;
            for (const levelData of this.selectedInstance.data) {
              if (this.selectedInstance.currentLevel == levelData.level && levelData.overallPercent == 100)
                this.canLevelUp = true
              //Add rule type for each level rule
              if (templateRulesDefinitions && templateRulesDefinitions[levelData.level]) {
                for (let instanceRule of levelData.instanceRules) {
                  if (templateRulesDefinitions[levelData.level][instanceRule.ruleId])
                    instanceRule.ruleType = templateRulesDefinitions[levelData.level][instanceRule.ruleId].ruleType;
                }
              }
            }
          }
        });
      this.getSyllabusTemplate(instance);
    },
    isCheckinsRule(ruleType) {
      if (ruleType && ruleType.indexOf('checkins') >= 0)
        return true;
      return false;
    },
    getSyllabusInstances() {
      Api()
        .get(
          "syllabus/academy/" + this.$store.state.managedAcademy + "/instances/member/" +
          this.memberId)
        .then((response) => {
          this.loading = false;
          if (response.data && response.data.instances) {
            if (response.data.completedInstances && response.data.completedInstances.length > 0)
              this.syllabusInstances = response.data.completedInstances;
            else
              this.syllabusInstances = null;
          }
        });
    },
  },
};
</script>

<style scoped>
.data-panel {
  overflow: auto;
  max-height: 500px;
}

.bday-floater {
  left: 1%;
  position: absolute;
}

.syllabus-text-input {
  max-width: 40px
}

.field-description {
  margin-top: -15px
}
</style>
